import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom'; 
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  Avatar,
  Snackbar,
  Divider // <--- ECCOLO! L'ho aggiunto qui
} from '@mui/material';
import { styled } from "@mui/system";
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

// Icone
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy'; // Opzionale se vuoi usarlo al posto di WhatsApp
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

// --- STYLED COMPONENTS ---

const MainContainer = styled(Box)({
  minHeight: "80vh",
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: "20px",
  background: "#F8F9FA",
});

const ModernCard = styled(Paper)({
  background: "#FFFFFF",
  borderRadius: "24px",
  boxShadow: "0px 10px 40px rgba(0,0,0,0.06)",
  padding: "40px",
  maxWidth: "500px",
  width: "100%",
  position: 'relative',
  overflow: 'hidden'
});

const HeaderIcon = styled(Avatar)({
  width: 60,
  height: 60,
  margin: "0 auto 20px",
  background: "linear-gradient(135deg, #a32b2b 0%, #c0392b 100%)",
  boxShadow: "0 4px 15px rgba(163, 43, 43, 0.4)",
});

const inputStyle = {
  '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff' }
};

export default function NewUserForm() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [toastOpen, setToastOpen] = useState(false);

  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (!fullName || !email) {
        setError("Nome ed Email sono obbligatori.");
        setLoading(false);
        return;
    }

    try {
      const usersCollectionRef = collection(db, 'users');
      await addDoc(usersCollectionRef, {
        fullName: fullName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone,
        regolamentoAccettato: false,
        dataAccettazioneRegolamento: null,
        createdAt: serverTimestamp(),
      });
      setSuccess(true);
    } catch (err) {
      console.error("Errore:", err);
      setError("Errore server. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const copyInvite = () => {
    const text = `Ciao ${fullName}! Ti ho pre-registrato sul portale SoulFit Hybrid Center. Scarica l'app e registrati usando la mail: ${email}`;
    navigator.clipboard.writeText(text);
    setToastOpen(true);
  };

  // --- SCHERMATA DI SUCCESSO ---
  if (success) {
    return (
      <MainContainer>
        <ModernCard sx={{ textAlign: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 80, color: '#2ecc71', mb: 2 }} />
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: '#2d3436' }}>
            Fatto!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Anagrafica creata per <strong>{fullName}</strong>.<br/>
            Ora l'utente deve scaricare l'app e registrarsi con la mail inserita.
          </Typography>
          
          <Stack spacing={2}>
            {/* Bottone Copia Invito */}
            <Button
                variant="contained"
                fullWidth
                color="success"
                onClick={copyInvite}
                startIcon={<WhatsAppIcon />}
                sx={{ 
                    borderRadius: '12px', 
                    bgcolor: '#25D366', 
                    fontWeight: 'bold',
                    boxShadow: '0 4px 10px rgba(37, 211, 102, 0.3)',
                    '&:hover': { bgcolor: '#128C7E' }
                }}
            >
                Copia Invito WhatsApp
            </Button>

            <Divider sx={{ my: 1 }}>o</Divider>

            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                 setSuccess(false);
                 setFullName('');
                 setEmail('');
                 setPhone('');
              }}
              startIcon={<PersonAddAlt1Icon />}
              sx={{ borderRadius: '12px', borderColor: '#a32b2b', color: '#a32b2b' }}
            >
              Censisci Altro Utente
            </Button>
            
            <Button
              variant="text"
              fullWidth
              onClick={() => navigate('/')}
              sx={{ color: '#636e72' }}
            >
              Torna alla Dashboard
            </Button>
          </Stack>
        </ModernCard>
        
        {/* Feedback visivo "Copiato" */}
        <Snackbar
            open={toastOpen}
            autoHideDuration={2000}
            onClose={() => setToastOpen(false)}
            message="Testo invito copiato negli appunti!"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </MainContainer>
    );
  }

  // --- FORM DI INSERIMENTO ---
  return (
    <MainContainer>
      <ModernCard>
        <Button 
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackIcon />}
            sx={{ position: 'absolute', top: 20, left: 20, minWidth: 0, p: 1, color: '#b2bec3' }}
        >
            Indietro
        </Button>

        <Box sx={{ textAlign: 'center', mb: 4, mt: 2 }}>
            <HeaderIcon>
                <PersonAddAlt1Icon sx={{ fontSize: 30 }} />
            </HeaderIcon>
            <Typography variant="h5" fontWeight="800" sx={{ color: "#2d3436" }}>
              Nuovo Iscritto
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Inserisci i dati per creare l'anagrafica.
            </Typography>
        </Box>

        <form onSubmit={handleRegister}>
          <Stack spacing={3}>
            <TextField
              fullWidth label="Nome e Cognome" placeholder="Es. Mario Rossi"
              value={fullName} onChange={(e) => setFullName(e.target.value)}
              required sx={inputStyle}
            />
            <TextField
              fullWidth label="Email" type="email" placeholder="mario@mail.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              required sx={inputStyle}
            />
            <TextField
              fullWidth label="Telefono" type="tel" placeholder="333 1234567"
              value={phone} onChange={(e) => setPhone(e.target.value)}
              sx={inputStyle}
            />

            {error && (
              <Alert severity="error" sx={{ borderRadius: '12px' }}>{error}</Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              size="large"
              sx={{ 
                bgcolor: '#a32b2b', 
                borderRadius: '12px',
                py: 1.8,
                fontSize: '1rem',
                fontWeight: 'bold',
                boxShadow: '0 4px 14px 0 rgba(163, 43, 43, 0.39)',
                '&:hover': { bgcolor: '#802222' } 
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'REGISTRA UTENTE'}
            </Button>
          </Stack>
        </form>
      </ModernCard>
    </MainContainer>
  );
}